import { useEffect, useState } from "react";
import { toAbsoluteUrl, toConfiguredAbsoluteUrl } from "../config/api";

/** Official WhatsApp speech-bubble mark (24×24 viewBox). */
export const WHATSAPP_ICON_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z";


function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function sitePath(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname.split(/[?#]/)[0];
    } catch {
      return "";
    }
  }
  const path = (raw.startsWith("/") ? raw : `/${raw}`).split(/[?#]/)[0];
  if (path === "/") return "/";
  return path.replace(/\/+$/, "");
}

/** Full public URL for WhatsApp. Uses SITE_URL when set; otherwise the current origin. Never invents a host. */
export function publicEnquiryUrl(path) {
  const configured = toConfiguredAbsoluteUrl(path);
  if (/^https?:\/\//i.test(configured)) return configured;
  const absolute = toAbsoluteUrl(configured || path);
  return /^https?:\/\//i.test(absolute) ? absolute : "";
}

export function enquiryPathKind(pathname) {
  const path = sitePath(pathname);
  if (/^\/category\/[^/]+\/[^/]+$/.test(path)) return { kind: "product", path };
  if (/^\/category\/[^/]+$/.test(path)) return { kind: "category", path };
  if (/^\/deliveries\/\d{6}\/[^/]+$/.test(path) || /^\/deliveries\/[^/]+$/.test(path)) {
    return { kind: "delivery", path };
  }
  if (path === "/deliveries" || path.startsWith("/deliveries/")) return { kind: "deliveries", path: "" };
  return { kind: "generic", path: "" };
}

export function shouldAttachEnquiryPage(pathname) {
  const { kind } = enquiryPathKind(pathname);
  return kind === "product" || kind === "category" || kind === "delivery";
}

export function enquiryContextFromState(state) {
  const productName = cleanText(state?.productName);
  const categorySlug = cleanText(state?.categorySlug);
  const slug = cleanText(state?.slug);
  const categoryName = cleanText(state?.categoryName);
  const deliveryTitle = cleanText(state?.deliveryTitle);
  const deliveryPath = sitePath(state?.deliveryHref);
  if (categorySlug && slug) {
    return { title: productName, path: `/category/${categorySlug}/${slug}` };
  }
  if (categorySlug) {
    return { title: categoryName || productName, path: `/category/${categorySlug}` };
  }
  if (deliveryPath || deliveryTitle) {
    return { title: deliveryTitle, path: deliveryPath };
  }
  return { title: productName || categoryName, path: "" };
}

export function whatsappEnquiryMessage({ siteName, title, path } = {}) {
  const site = cleanText(siteName);
  const hello = site ? `Hello ${site}` : "Hello";
  const rawPath = sitePath(path);
  const page = enquiryPathKind(rawPath);
  const isDelivery = page.kind === "delivery" || page.kind === "deliveries" || rawPath.startsWith("/deliveries");
  let message = isDelivery
    ? `${hello}, I would like to enquire about material sourcing and delivery to my site.`
    : `${hello}, I would like to enquire about your products and supply services.`;
  const name = cleanText(title);
  if (name) message += ` I am looking at ${name}.`;
  const attachPath = page.path || (shouldAttachEnquiryPage(rawPath) ? rawPath : "");
  const url = attachPath ? publicEnquiryUrl(attachPath) : "";
  if (url) message += ` ${url}`;
  return message;
}

export function whatsappEnquiryHref({ number, siteName, title, path } = {}) {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "";
  return `https://wa.me/${digits}?text=${encodeURIComponent(whatsappEnquiryMessage({ siteName, title, path }))}`;
}

export function readVisiblePageTitle() {
  if (typeof document === "undefined") return "";
  const h1 = document.querySelector("main h1");
  const fromH1 = cleanText(h1?.textContent?.replace(/\s+/g, " "));
  if (!fromH1 || /unable to load|couldn.?t find|page not found|^loading /i.test(fromH1)) return "";
  return fromH1;
}

export function useEnquiryPageTitle(pathname) {
  const [title, setTitle] = useState("");
  useEffect(() => {
    let tries = 0;
    let timer;
    const read = () => {
      const next = readVisiblePageTitle();
      if (next || tries >= 24) {
        setTitle(next);
        return;
      }
      tries += 1;
      timer = window.setTimeout(read, 125);
    };
    setTitle("");
    read();
    return () => window.clearTimeout(timer);
  }, [pathname]);
  return title;
}
